import { TrashIcon } from "@cynthai/icons";
import NiceModal from "@ebay/nice-modal-react";
import { useCallback, useState } from "react";

import Button from "../Button";
import { useNiceModal } from "../hooks/useNiceModal";
import { m } from "../paraglide/messages";
import type { BaseModalProps } from "../types";
import BaseModal from "./BaseModal";

interface DeleteModalProps extends BaseModalProps {
  title: string;
  description: string;
  onDelete: () => void | Promise<void>;
}

const DeleteModal = NiceModal.create((props: DeleteModalProps) => {
  const modalProps = useNiceModal();
  const { onClose, onOpenChange } = props;
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (isDeleting) return;

    try {
      setIsDeleting(true);
      await props.onDelete?.();
      modalProps.onClose();
      onClose?.();
    } catch {
      // keep the modal open for retry; surfacing the error (toast etc.)
      // is the onDelete caller's responsibility
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, modalProps, onClose, props]);

  return (
    <BaseModal
      title={props.title}
      size="sm"
      description={props.description}
      footer={
        <>
          <Button
            color="secondary"
            isDisabled={isDeleting}
            onPress={() => {
              modalProps.onClose();
              onClose?.();
            }}
            size="lg"
          >
            {m.cancel_button_label()}
          </Button>
          <Button
            color="danger"
            isDisabled={isDeleting}
            isLoading={isDeleting}
            onPress={handleConfirm}
            size="lg"
          >
            <TrashIcon width={20} height={20} />
            {m.confirm_button_label()}
          </Button>
        </>
      }
      {...modalProps}
      onClose={() => {
        if (isDeleting) return;
        modalProps.onClose();
        onClose?.();
      }}
      onOpenChange={(open) => {
        if (isDeleting) return;
        modalProps.onOpenChange(open);
        onOpenChange?.(open);
      }}
    ></BaseModal>
  );
});

export default DeleteModal;
